import {
  COMMON_AUTODESK_URL_TO_CACHE_FOR_CHROME,
  COMMON_AUTODESK_URL_TO_CACHE_FOR_SAFARI,
  PROJECT_RESOURCE,
} from "constants/cache";
import { IRegisterCacheModule } from "./types";
import { getGeneratedViewList } from "apiClient/v2/bims/bimViewApi";
import { authApi } from "apiClient/v2";
import { doRefreshToken } from "utils/authen";
import { isSafari } from "react-device-detect";
import { fetchRequest, removeParamFromUrl } from "utils/fetch";
import { getDataFamilyInstance } from "utils/forge/s3Data";
import { forgeApi } from "apiClient/v2";
import { userApi } from "apiClient/v2";
import { userSettingApi } from "apiClient/v2";
import { projectApi } from "apiClient/v2";
import { bimFileApi } from "apiClient/v2";
import { partnerCompanyApi } from "apiClient/v2";
import { inspectionTypeApi } from "apiClient/v2";
import { getCombinedViewList } from "apiClient/v2/bims/bimViewApi";
import { getDerivativesByVersionId } from "apiClient/v2/forgeApi";
import { MODERN_SCREENSHOT_WORKER_URL } from "constants/downloadPDF";
import { retryFetch } from "utils/fetch";
import { getAECData } from "utils/forge/forgeApiData";
import { cacheDefaultData, getUrlsOfModel } from "utils/cache";
import { CACHE_QUERY_PARAM } from "constants/serviceWorker";
import { sleep } from "utils/common";
import { getForgeToken } from "utils/forge";
import { LEVEL_OTHER } from "constants/forge";

export const cacheModules: IRegisterCacheModule[] = [
  {
    key: PROJECT_RESOURCE.defaultData,
    handler: cacheDefaultData,
  },
  {
    key: PROJECT_RESOURCE.refreshToken,
    handler: () => doRefreshToken(true),
  },
  {
    key: PROJECT_RESOURCE.currentUser,
    handler: () => authApi.getCurrentUser(true),
  },
  {
    key: PROJECT_RESOURCE.autoDesk,
    handler: async () => {
      const commonUrls = isSafari
        ? COMMON_AUTODESK_URL_TO_CACHE_FOR_SAFARI
        : COMMON_AUTODESK_URL_TO_CACHE_FOR_CHROME;
      await Promise.all(commonUrls.map((url) => fetchRequest(url, {}, true)));
    },
  },
  {
    key: PROJECT_RESOURCE.forgeData,
    handler: async ({ bimFileId, version, project }) => {
      const results = await Promise.all(
        Object.entries(project.levelData ?? {}).map(([level, data]) => {
          return getDataFamilyInstance({
            bimFileId,
            version,
            level: data.guid ? level : LEVEL_OTHER.label,
            shouldCache: true,
          });
        })
      );

      return results.every((item) => !!item);
    },
  },
  {
    key: PROJECT_RESOURCE.aecData,
    handler: async ({ bimFileId, version }) => {
      const res = await getAECData(`${bimFileId}?version=${version}`, true);

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.modernScreenshot,
    handler: async () => {
      const res = await retryFetch(
        `${process.env.PUBLIC_URL}${MODERN_SCREENSHOT_WORKER_URL}`
      );

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.versions,
    handler: async ({ bimFileId, project }) => {
      const { data: res } = await forgeApi.getVersionsByBimFileId({
        projectId: project.projectId,
        bimFileId: bimFileId.replace("fs.file:vf.", "dm.lineage:"),
        shouldCache: true,
      });

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.listUser,
    handler: async () => {
      const { data: res } = await userApi.getUserList({}, true);

      return !!res?.length;
    },
  },
  {
    key: PROJECT_RESOURCE.userSetting,
    handler: async () => {
      const res = await userSettingApi.getUserSetting(true);

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.project,
    handler: async () => {
      await forgeApi.getProjects(false, true);

      return true;
    },
  },
  {
    key: PROJECT_RESOURCE.projectDetail,
    handler: async ({ project }) => {
      const { data: res } = await projectApi.getProject(
        project.projectId,
        true
      );

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.partnerCompanies,
    handler: async ({ bimFileId }) => {
      const res = await partnerCompanyApi.getPartnerList({
        bimFileId,
        shouldCache: true,
      });

      return !!res;
    },
  },
  {
    key: PROJECT_RESOURCE.inspectionTypeList,
    handler: async ({ bimFileId, versionId, project }, cacheService) => {
      await inspectionTypeApi.getAllInspectionTypeWithCursor({
        shouldCache: true,
        bimFileId: encodeURIComponent(bimFileId),
        bimVersionId: versionId,
        projectId: project.projectId,
        cacheService,
      });

      return true;
    },
  },
  {
    key: PROJECT_RESOURCE.allView,
    handler: async ({ project, versionId }) => {
      const res = await forgeApi.getAllBimViewByVersionId(
        project.projectId,
        versionId,
        true
      );

      return !!res.data;
    },
  },
  {
    key: PROJECT_RESOURCE.allBimView,
    handler: ({ versionId }) => getGeneratedViewList(versionId, true),
  },
  {
    key: PROJECT_RESOURCE.bimViewList,
    handler: async ({ project, versionId }) => {
      const res = await getCombinedViewList(project.projectId, versionId, true);

      return !!res.data;
    },
  },
  {
    key: PROJECT_RESOURCE.derivative,
    handler: async ({ project, versionId }) => {
      const res = await getDerivativesByVersionId({
        projectId: project.projectId,
        versionId,
        shouldCache: true,
      });

      return !!res.data;
    },
  },
  {
    key: PROJECT_RESOURCE.modelData,
    handler: async ({ project, cachedUrls }, cacheService) => {
      // cache model
      const credentials = await getForgeToken(true);
      if (cacheService?.isCancel()) throw new Error();
      const projectDetailResponse = await bimFileApi.getProject(project.id);
      const modelUrls = await getUrlsOfModel(projectDetailResponse.data, true);
      if (cacheService?.isCancel()) throw new Error();
      const urlsToCache = modelUrls.filter((url) => {
        const urlNoCache = removeParamFromUrl(url, CACHE_QUERY_PARAM);

        return !cachedUrls.includes(urlNoCache);
      });
      let requests: Promise<any>[] = [];

      const optionRequests = {
        headers: { Authorization: `Bearer ${credentials.accessToken}` },
      };

      for (let i = 0; i < urlsToCache.length; i++) {
        const url = urlsToCache[i];
        requests.push(fetchRequest(url, optionRequests, true));
        if (cacheService?.isCancel()) throw new Error();
        if (requests.length >= 10) {
          await Promise.all(requests);
          await sleep(50);
          requests = [];
        }
      }
    },
  },
];
